import axios from 'axios';

import {
  Challenger,
  ConfirmVerifyEmail,
  ContextResultsArgs,
  ContextResultsResponse,
  ContextsResponse,
  CreateVerifyEmail,
  GetTournamentRegister,
  GetWarriorAttackArgs,
  GetWarriorAttackResponse,
  PairingsResponse,
  ResultsByWarriorArgs,
  ResultsByWarriorResponse,
  TournamentByeArgsByOwner,
  TournamentByeResponse,
  TournamentByes,
  TournamentOwnerPairingsArgs,
  TournamentPairingsVerboseArgs,
  TournamentPairingsVerboseResponse,
  TournamentRegister,
  TournamentRegisterSuccessResponse,
  TournamentWinners,
} from '~/../api/common.types';
import { TOURNAMENTS } from '~/constants';

const apiHost = process.env.NEXT_PUBLIC_API_HOST;
const apiCache = process.env.NEXT_PUBLIC_API_CACHE;
const BASE_CDN = process.env.NEXT_PUBLIC_BASE_CDN;

class API {
  baseURL = apiHost;
  apiCache = apiCache;

  // send email for user to verify
  async createVerifyEmail(payload: CreateVerifyEmail): Promise<any> {
    const url = '/api/emailverifier';
    const { data } = await axios.post(url, {
      email: payload.email,
      address: payload.address,
    });
    return data;
  }

  async confirmEmailVerified(payload: ConfirmVerifyEmail): Promise<any> {
    const url = '/api/emailverifier';
    const params = {
      address: payload.address,
      token: payload.code, // email code, not wallet token
    };
    const { data } = await axios.get(url, { params });
    return data;
  }

  async tournamentRegister(
    payload: TournamentRegister
  ): Promise<TournamentRegisterSuccessResponse> {
    const { data } = await axios({
      url: `${this.baseURL}/contexts/${TOURNAMENTS.valentines.apiContext}/register`,
      method: 'post',
      headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${payload.token}`,
      },
      data: {
        registration: {
          context_id: TOURNAMENTS.valentines.apiContext,
          wallet_address: payload.walletAddress,
        },
      },
    });
    return data;
  }

  async getTournamentRegister(
    payload: GetTournamentRegister
  ): Promise<TournamentRegisterSuccessResponse> {
    const { data } = await axios({
      url: `${this.baseURL}/contexts/${TOURNAMENTS.valentines.apiContext}/registered`,
      method: 'get',
      headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${payload.token}`,
      },
    });
    return data;
  }

  async getTournamentOwnerPairings(
    payload: TournamentOwnerPairingsArgs
  ): Promise<PairingsResponse> {
    const url = `${this.baseURL}/context/${payload.contextId}/state/${payload.battleId}/owner/${payload.walletAddress}/pairings`;
    const { data } = await axios({
      url: url,
      method: 'get',
    });
    return data.pairings;
  }

  async getWarriorAttack(
    payload: GetWarriorAttackArgs
  ): Promise<GetWarriorAttackResponse> {
    const url = `${this.baseURL}/context/${payload.contextId}/state/${payload.battleId}/warrior/${payload.warriorId}/attack`;
    const { data } = await axios({
      url: url,
      method: 'get',
      headers: {
        'Content-type': 'application/json',
        // eslint-disable-next-line quote-props
        Authorization: `Bearer ${payload.token}`,
      },
    });
    const attack = data?.attack ? data?.attack.toLowerCase() : null;
    return { attack };
  }

  async getResultsByWarrior(
    payload: ResultsByWarriorArgs
  ): Promise<ResultsByWarriorResponse> {
    const url = `${this.baseURL}/context/${payload.contextId}/state/${payload.battleId}/warrior/${payload.warriorId}/results`;
    const { data } = await axios({
      url: url,
      method: 'get',
    });
    return data;
  }

  async getResults(
    payload: ContextResultsArgs
  ): Promise<ContextResultsResponse> {
    const url = `${this.apiCache}/contexts/${payload.contextId}/states/${payload.battleId}/results`;
    const { data } = await axios({
      url: url,
      method: 'get',
    });
    return data;
  }

  async getPairingsVerbose(
    payload: TournamentPairingsVerboseArgs
  ): Promise<TournamentPairingsVerboseResponse> {
    const url = `${this.apiCache}/contexts/${payload.contextId}/states/${payload.battleId}/pairings.verbose`;
    const { data } = await axios({
      url: url,
      method: 'get',
    });
    return data;
  }

  async getTournamentByesByOwner(
    payload: TournamentByeArgsByOwner
  ): Promise<TournamentByeResponse> {
    const url = `${this.apiCache}/contexts/${payload.contextId}/states/${payload.battleId}/byes`;
    const { data: byes } = await axios({
      url: url,
      method: 'get',
    });
    const urlOwnership = `${this.apiCache}/ownership.json`;
    const { data: warriodIdsByOwner } = await axios({
      url: urlOwnership,
      method: 'get',
      headers: {
        'Content-type': 'application/json',
      },
    });
    const warriorsByesByOwner: [number] | [] = byes.filter(
      (byeWarriorId) =>
        warriodIdsByOwner[byeWarriorId].toLowerCase() ===
        payload.walletAddress.toLowerCase()
    );
    return warriorsByesByOwner;
  }

  async getTournamentByes(
    payload: TournamentByes
  ): Promise<TournamentByeResponse> {
    const url = `${this.apiCache}/contexts/${payload.contextId}/states/0/byes`;
    const { data } = await axios({
      url: url,
      method: 'get',
    });
    return data;
  }

  async getContexts(): Promise<ContextsResponse> {
    const url = `${apiHost}/contexts`;
    const { data } = await axios({
      url: url,
      method: 'get',
    });
    return data;
  }

  async getChallenger(id: string | number): Promise<Challenger> {
    const url = `${BASE_CDN}/${id}.json`;
    const { data: challenger } = await axios({
      url,
      method: 'get',
    });

    return challenger;
  }

  async getTournamentWinners(): Promise<TournamentWinners[]> {
    const url = '/api/tournament/winners';
    const { data: torunamentWinners } = await axios.get(url);

    return torunamentWinners;
  }
}

export const api = new API();
